import * as React from "react";
import Swal from "sweetalert2";

import Navbar from "./nav/Navbar";
import Footer from "../main/nav/Footer";
import { apiContext } from "../../Api";
import useAuth from "../main/Auth/useAuth";
import Biodata from "./component/Biodata";
import PieChartPublikasi from "./component/PieChartPublikasi";
import BarChartQuartile from "./component/BarChartQuartile";

const icons = {
  document: (
    <path d="M7 3h7l5 5v13H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Zm7 0v5h5M9 13h6M9 17h6" />
  ),
  chart: <path d="M4 19h16M7 16V9m5 7V5m5 11v-4" />,
  globe: (
    <path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm-8-9h16M12 3c2.2 2.4 3.3 5.4 3.3 9S14.2 18.6 12 21c-2.2-2.4-3.3-5.4-3.3-9S9.8 5.4 12 3Z" />
  ),
  arrow: <path d="M5 12h14m-6-6 6 6-6 6" />,
  user: <path d="M20 21a8 8 0 0 0-16 0M12 13a5 5 0 1 0 0-10 5 5 0 0 0 0 10Z" />,
};

function Icon({ name, className = "h-5 w-5" }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {icons[name]}
    </svg>
  );
}



function DashboardContent() {
  const { axios } = React.useContext(apiContext);
  const { user, profile } = useAuth();
  const userId = localStorage.getItem("user_id");
  const [publikasi, setPublikasi] = React.useState([]);
  const [syncing, setSyncing] = React.useState(false);

  const handleSyncSinta = async () => {
    setSyncing(true);
    Swal.fire({
      title: 'Mohon tunggu',
      text: 'Sedang menyinkronkan data skor SINTA...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const response = await axios.post('/profile/sync-sinta');
      Swal.fire({
        icon: 'success',
        title: 'Berhasil',
        text: response.data.message || 'Sinkronisasi skor SINTA berhasil.',
        timer: 2000,
        showConfirmButton: false
      });
      profile();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Gagal',
        text: error?.response?.data?.message || 'Terjadi kesalahan saat sinkronisasi skor SINTA.'
      });
    } finally {
      setSyncing(false);
    }
  };

  React.useEffect(() => {
    const fetchPublikasi = async () => {
      if (!userId) {
        return;
      }

      const response = await axios.get(`/publikasi-count/`);
      if (response.data) {
        setPublikasi(response.data);
      }
    };

    profile();
    fetchPublikasi();
    // profile berasal dari context lama dan belum di-memoize; menjaga efek tetap sekali per user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axios, userId]);

  return (
    <div className="min-h-screen bg-brand-soft text-brand-navy md:flex">
      <Navbar title="Dashboard" />

      <main className="min-w-0 flex-1 pt-20 md:pt-24 mt-3">
        <div className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
          <section className="relative overflow-hidden rounded-2xl bg-white shadow-soft sm:p-0">
            <div className="absolute top-0 inset-x-0 h-1.5 bg-gradient-to-r from-transparent via-brand-gold to-transparent opacity-90"></div>
            <div className="absolute inset-0 bg-brand-navy/5 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] opacity-50"></div>

            <div className="relative flex flex-col gap-6 p-6 lg:flex-row lg:items-center lg:justify-between lg:p-8">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-2 rounded-full bg-brand-gold/10 px-3 py-1.5 text-xs font-extrabold uppercase text-yellow-600 border border-brand-gold/20 shadow-sm">
                    <Icon name="user" className="h-3.5 w-3.5" />
                    Profil Peneliti
                  </span>
                  {user?.profile?.sinta && (
                    <button
                      type="button"
                      onClick={handleSyncSinta}
                      disabled={syncing}
                      className="inline-flex items-center gap-2 rounded-full bg-brand-navy hover:bg-brand-blue px-3.5 py-1.5 text-xs font-extrabold text-white transition disabled:opacity-50 shadow-sm"
                    >
                      <i className={`fas fa-sync-alt ${syncing ? 'animate-spin' : ''}`} />
                      Sinkron SINTA
                    </button>
                  )}
                </div>
                <h1 className="mt-5 font-display text-3xl font-extrabold leading-tight text-brand-navy sm:text-4xl">
                  {user?.name || "Dashboard Publikasi"}
                </h1>
                <p className="mt-3 max-w-xl text-sm leading-7 text-slate-600 sm:text-base">
                  Kelola identitas publikasi pribadi, pantau kinerja riset, dan lengkapi profil akademik Anda.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-4 mt-6 lg:mt-0">
                <div className="flex-1 min-w-[150px] flex flex-col items-center justify-center rounded-xl border-2 border-brand-green/20 bg-brand-green/5 p-4 text-center shadow-sm transition hover:border-brand-green/40">
                  <span className="block text-[11px] font-bold uppercase tracking-wider text-brand-green">
                    Total Publikasi
                  </span>
                  <strong className="mt-1 block font-display text-3xl font-extrabold text-brand-navy">
                    {publikasi || 0}
                  </strong>
                </div>

                {user?.profile?.sinta_overall !== null && user?.profile?.sinta_overall !== undefined && (
                  <div className="flex-1 min-w-[150px] flex flex-col items-center justify-center rounded-xl border-2 border-brand-gold/20 bg-brand-gold/5 p-4 text-center shadow-sm transition hover:border-brand-gold/40">
                    <span className="block text-[11px] font-bold uppercase tracking-wider text-brand-gold">
                      SINTA Overall
                    </span>
                    <strong className="mt-1 block font-display text-3xl font-extrabold text-brand-navy">
                      {user.profile.sinta_overall}
                    </strong>
                  </div>
                )}

                {user?.profile?.sinta_3yr !== null && user?.profile?.sinta_3yr !== undefined && (
                  <div className="flex-1 min-w-[150px] flex flex-col items-center justify-center rounded-xl border-2 border-blue-200 bg-blue-50/50 p-4 text-center shadow-sm transition hover:border-blue-300">
                    <span className="block text-[11px] font-bold uppercase tracking-wider text-blue-600">
                      SINTA 3 Yr
                    </span>
                    <strong className="mt-1 block font-display text-3xl font-extrabold text-brand-navy">
                      {user.profile.sinta_3yr}
                    </strong>
                  </div>
                )}

                {user?.profile?.sinta_affil !== null && user?.profile?.sinta_affil !== undefined && (
                  <div className="flex-1 min-w-[150px] flex flex-col items-center justify-center rounded-xl border-2 border-purple-200 bg-purple-50/50 p-4 text-center shadow-sm transition hover:border-purple-300">
                    <span className="block text-[11px] font-bold uppercase tracking-wider text-purple-600">
                      Affil Score
                    </span>
                    <strong className="mt-1 block font-display text-3xl font-extrabold text-brand-navy">
                      {user.profile.sinta_affil}
                    </strong>
                  </div>
                )}
              </div>
            </div>
          </section>

          <section className="mt-6 grid gap-4 md:grid-cols-2" id="grafik">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-soft transition hover:-translate-y-1 hover:border-brand-green hover:shadow-lg" id="pie-chart">
              <PieChartPublikasi user={user} />
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-soft transition hover:-translate-y-1 hover:border-brand-green hover:shadow-lg" id="bar-chart">
              <BarChartQuartile />
            </div>
          </section>

          <section className="mt-6 grid gap-5 lg:grid-cols-4">
            <Biodata user={user} />
          </section>

          <div className="mt-8 rounded-lg bg-brand-navy text-white">
            <Footer />
          </div>
        </div>
      </main>
    </div>
  );
}

export default function Dashboard() {
  return <DashboardContent />;
}
